// Diff view: per-cell (supply − demand). Supply = humans in the active
// projection context who clear the cell's strictest bar on at least one
// demanded skill. Color: red ramp for deficits, teal/green ramp for
// covered or surplus. Cells without demand stay empty so attention rides
// on the cells the roadmap actually consumes.

import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { Engineer } from '@lib/api-types/engineers';
import type { RatingValue } from '@lib/api-types/ratings';
import { Fragment, useMemo } from 'react';

import { EmptyState } from '../../components/EmptyState.tsx';
import { MatrixCell } from '../../components/MatrixCell.tsx';
import {
  ColumnHeader,
  GroupedCompLabel,
  NestConnector,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  buildSkillFunctions,
  compRowHeight,
  isCompactCatalog,
  isDefaultVisibleSkill,
  isInstitutional,
} from '../../data-views.ts';
import {
  countQualifiedEngineers,
  type MergedDerivedContext,
  type MergedSkill,
  type ProjectedTalentColumn,
  type ProjectImpactCell,
  type ProjectView,
  type RoadmapProjectionContext,
} from '../../roadmap-data.ts';

import {
  compServesFunctionWithNew,
  isNewFunction,
  reqKey,
  skillCellServesFunction,
} from './req-key.ts';

function engineerKind(eng: Engineer | ProjectedTalentColumn): 'human' | 'agent' {
  return 'kind' in eng && eng.kind === 'agent' ? 'agent' : 'human';
}

/**
 * How many demand slots in a (comp, fn) impact cell target `skillId` — the
 * per-skill share of the cell's demand. Flat mode uses this in place of the
 * old hardcoded "1 slot per cell": a roadmap that demands a skill across
 * several profiles/projects should net its supply against that real demand,
 * not a flat 1, otherwise flat surpluses read far higher than the grouped
 * view's (which nets against the full profile count, `cell.count`). Counts
 * aggregate `contributions` when present, else per-project `demands`.
 */
function perSkillDemand(
  cell: ProjectImpactCell | undefined,
  skillId: string
): number {
  if (!cell) return 0;
  const items = cell.contributions ?? cell.demands;
  let n = 0;
  for (const it of items) if (it.skill === skillId) n++;
  return n;
}

/** Humans (excluding agents) in `ctx` whose rating on `skillId` clears `threshold`. */
function humansAtThreshold(
  ctx: RoadmapProjectionContext,
  skillId: string,
  threshold: RatingValue
): string[] {
  const out: string[] = [];
  for (const eng of ctx.teamColumns) {
    if (engineerKind(eng) === 'agent') continue;
    const rating = ctx.ratings.get(eng.id)?.get(skillId);
    if (rating !== undefined && rating >= threshold) out.push(eng.display_name);
  }
  return out;
}

// Magnitude → ramp level. Buckets mirror the testbed renderer so the
// same data lights cells at the same intensity across both surfaces.
function negLevel(magnitude: number): 1 | 2 | 3 | 4 {
  if (magnitude >= 8) return 4;
  if (magnitude >= 4) return 3;
  if (magnitude >= 2) return 2;
  return 1;
}
function posLevel(magnitude: number): 1 | 2 | 3 | 4 {
  if (magnitude >= 4) return 4;
  if (magnitude >= 2) return 3;
  if (magnitude >= 1) return 2;
  return 1;
}

export function RoadmapDiffGrid({
  project,
  ctx,
  grouped,
  showLibraries,
}: {
  project: ProjectView;
  ctx: RoadmapProjectionContext;
  grouped: boolean;
  showLibraries: boolean;
}) {
  const { mergedDerivedContexts, mergedFunctions, requirementsByRole, roles } =
    project;

  // Keep the per-comp row sizing aligned with Impact so the two grids
  // line up vertically when the user toggles between them. Library
  // skills hidden by default; flip via the FilterViewBar toggle.
  const visibleComps = useMemo(
    () =>
      mergedDerivedContexts
        .map(c => ({
          ...c,
          skills: showLibraries
            ? c.skills
            : c.skills.filter(isDefaultVisibleSkill),
        }))
        .filter(c => c.skills.length > 0),
    [mergedDerivedContexts, showLibraries]
  );

  const skillDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of mergedDerivedContexts) {
      for (const s of c.skills) map.set(s.id, s.display);
    }
    return map;
  }, [mergedDerivedContexts]);

  // Skill → its catalog function footprint (union across every derivedContext
  // that lists the skill) — the same source the Resilience map and Impact
  // grid use to decide which (comp/skill × function) cells exist.
  const skillFunctions = useMemo(
    () => buildSkillFunctions(mergedDerivedContexts),
    [mergedDerivedContexts],
  );

  // Per-skill required level for flat mode. Aggregate ProjectViews keep
  // their top-level roles/requirementsByRole empty (per-project breakdown
  // lives in projectGroups + maxRequirements), so derive from there in
  // aggregate mode — mirrors the same logic the Impact grid uses.
  // Keyed by composite reqKey(comp, skill) — see RoadmapImpactGrid.
  const requiredLevelBySkill = useMemo(() => {
    const map = new Map<string, RatingValue>();
    if (project.isAggregate && project.maxRequirements) {
      for (const comp of mergedDerivedContexts) {
        for (const s of comp.skills) {
          const level = project.maxRequirements[s.id];
          if (level === undefined) continue;
          map.set(reqKey(comp.id, s.id), level);
        }
      }
      return map;
    }
    for (const role of roles) {
      const reqs = requirementsByRole[role.id] ?? {};
      for (const [key, req] of Object.entries(reqs)) {
        const prev = map.get(key);
        if (prev === undefined || req.level > prev) map.set(key, req.level);
      }
    }
    return map;
  }, [project, requirementsByRole, roles, mergedDerivedContexts]);

  const gridStyle = useMemo(() => {
    const base: Record<string, string> = {
      gridTemplateColumns: `130px 42px 6px 140px repeat(${mergedFunctions.length}, minmax(72px, 1fr))`,
    };
    if (grouped) {
      const compact = isCompactCatalog(mergedDerivedContexts);
      const heights = visibleComps
        .map(c => `${compRowHeight(c.skills.length, compact)}px`)
        .join(' ');
      base.gridTemplateRows = `auto ${heights}`;
    }
    return base;
  }, [mergedFunctions.length, grouped, visibleComps, mergedDerivedContexts]);

  if (visibleComps.length === 0 || mergedFunctions.length === 0) {
    return <EmptyState title='No demand in this selection' />;
  }

  const isAggregate = project.isAggregate === true;

  return (
    <div className='snap-grid' style={gridStyle}>
      <div />
      <div />
      <div />
      <div />
      {mergedFunctions.map(fn => (
        <ColumnHeader key={fn.id} display={fn.display} subtitle={fn.subtitle} />
      ))}

      {grouped
        ? visibleComps.map(comp => (
            <Fragment key={comp.id}>
              <GroupedCompLabel
                propCount={comp.skills.filter(isInstitutional).length}
                name={
                  <TruncatedLabel
                    text={comp.display}
                    max={22}
                    isNew={comp.is_overlay}
                  />
                }
                count={
                  <>
                    {comp.skills.length} skill
                    {comp.skills.length === 1 ? '' : 's'}
                  </>
                }
              />
              {mergedFunctions.map(fn => (
                <DiffCell
                  key={fn.id}
                  comp={comp}
                  fn={fn}
                  cell={project.impactGrid[comp.id]?.[fn.id]}
                  compServesFn={compServesFunctionWithNew(
                    comp,
                    fn.id,
                    skillFunctions,
                    project.newSkillFunctions,
                  )}
                  ctx={ctx}
                  skillDisplayById={skillDisplayById}
                  isAggregate={isAggregate}
                  projectDisplay={project.display}
                />
              ))}
            </Fragment>
          ))
        : visibleComps.map(comp => (
            <Fragment key={comp.id}>
              {comp.skills.map((skill, i) => {
                // Flat (skill) rows light each function THIS skill maps to —
                // its own catalog subset plus any net-new (beyond-catalog)
                // associations — matching the flat Impact view, with the same
                // empty-functions fallback the grouped path uses.
                const compServesFn = (fn: BusinessFunction) =>
                  skillCellServesFunction(
                    comp,
                    skill.id,
                    fn.id,
                    skillFunctions,
                    project.newSkillFunctions,
                  );
                return (
                  <Fragment key={skill.id}>
                    <div className='snap-grid__row-label snap-grid__row-label--muted'>
                      {i === 0 ? (
                        <TruncatedLabel
                          text={comp.display}
                          isNew={comp.is_overlay}
                        />
                      ) : null}
                    </div>
                    <div className='snap-grid__row-label snap-grid__row-label--conn'>
                      <NestConnector
                        variant={nestVariant(i, comp.skills.length)}
                      />
                    </div>
                    <div className='snap-grid__row-label snap-grid__row-label--prop'>
                      <PropIndicator institutional={isInstitutional(skill)} />
                    </div>
                    <div className='snap-grid__row-label'>
                      <TruncatedLabel
                        text={skill.display}
                        isNew={skill.is_overlay}
                      />
                    </div>
                    {mergedFunctions.map(fn => (
                      <SkillDiffCell
                        key={fn.id}
                        skill={skill}
                        fn={fn}
                        compServesFn={compServesFn(fn)}
                        isNewFunction={isNewFunction(
                          comp.id,
                          skill.id,
                          fn.id,
                          project.newSkillFunctions,
                        )}
                        requiredLevel={requiredLevelBySkill.get(reqKey(comp.id, skill.id)) ?? null}
                        demand={perSkillDemand(
                          project.impactGrid[comp.id]?.[fn.id],
                          skill.id,
                        )}
                        ctx={ctx}
                        rowIsOverlay={comp.is_overlay || skill.is_overlay}
                      />
                    ))}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
    </div>
  );
}

/**
 * Flat-mode (per-skill) diff cell. Demand is one slot per cell — if the
 * skill is required, diff = supply − 1; if not, the cell falls into the
 * "unused capacity" treatment (diff = supply − 0) so the user can spot
 * skills that could absorb work from stressed cells nearby.
 */
function SkillDiffCell({
  skill,
  fn,
  compServesFn,
  isNewFunction: isNew,
  requiredLevel,
  demand: rawDemand,
  ctx,
  rowIsOverlay,
}: {
  skill: MergedSkill;
  fn: BusinessFunction;
  compServesFn: boolean;
  /** This (skill × function) was assigned beyond the catalog footprint. */
  isNewFunction: boolean;
  requiredLevel: RatingValue | null;
  /** Per-skill demand slots for this cell (0 when undemanded). */
  demand: number;
  ctx: RoadmapProjectionContext;
  rowIsOverlay: boolean;
}) {
  const overlayClass = rowIsOverlay || isNew ? 'is-overlay-cell' : undefined;
  if (!compServesFn) {
    return (
      <MatrixCell
        value={null}
        levelClass='na'
        tooltip={null}
        extraClass={overlayClass}
      />
    );
  }

  if (requiredLevel !== null) {
    const qualified = humansAtThreshold(ctx, skill.id, requiredLevel);
    const supply = qualified.length;
    // Net against the real per-skill demand, not a flat 1. A cell that's
    // required always has at least one demand slot, so floor at 1 in the
    // (defensive) case where the impact cell didn't carry a matching
    // demand entry.
    const demand = Math.max(1, rawDemand);
    const diff = supply - demand;
    const level = diff < 0 ? negLevel(Math.abs(diff)) : posLevel(diff);
    const sign = diff > 0 ? '+' : '';
    const levelClass = `diff ${diff < 0 ? 'neg' : 'pos'} lvl-${level}`;
    return (
      <MatrixCell
        value={`${sign}${diff}` as unknown as number}
        levelClass={levelClass}
        extraClass={overlayClass}
        tooltip={{
          title: `${skill.display} · ${fn.display}`,
          body: [
            `Supply ${supply} · Demand ${demand}`,
            `Required: ${RATING_LABELS[requiredLevel]}`,
            qualified.length > 0
              ? `Qualified: ${qualified.join(', ')}`
              : 'Qualified: (none clear the bar)',
          ].join('\n'),
        }}
      />
    );
  }

  // Skill carries no roadmap demand → no project impact on this cell, so
  // there's nothing to net against resilience. The Diff view is
  // (project impact − resilience); a cell with zero impact stays empty so
  // attention rides on the cells the roadmap actually consumes (see the
  // module header). Surfacing the talent-map headcount here as
  // "+N unused capacity" was the source of the abnormally-high flat-mode
  // values — it pulled raw resilience data into undemanded cells.
  return (
    <MatrixCell
      value={0}
      levelClass='diff empty'
      extraClass={overlayClass}
      tooltip={{
        title: `${skill.display} · ${fn.display}`,
        body: 'No roadmap demand on this skill.',
      }}
    />
  );
}

function DiffCell({
  comp,
  fn,
  cell,
  compServesFn,
  ctx,
  skillDisplayById,
  isAggregate,
  projectDisplay,
}: {
  comp: MergedDerivedContext;
  fn: BusinessFunction;
  cell: ProjectImpactCell | undefined;
  compServesFn: boolean;
  ctx: RoadmapProjectionContext;
  skillDisplayById: Map<string, string>;
  isAggregate: boolean;
  projectDisplay: string;
}) {
  if (!compServesFn) {
    return (
      <MatrixCell
        value={null}
        levelClass='na'
        tooltip={null}
        extraClass={`is-merged${comp.is_overlay ? ' is-overlay-cell' : ''}`}
      />
    );
  }

  // No roadmap demand on this cell → no project impact, so there's nothing
  // to net against resilience. The Diff view is (project impact −
  // resilience); a zero-impact cell stays empty so attention rides on the
  // cells the roadmap actually consumes (see the module header). The old
  // "+N unused capacity" readout pulled raw talent-map headcount into
  // undemanded cells — kept here in lockstep with the flat path so both
  // modes read consistently.
  if (!cell || cell.skillCount === 0) {
    return (
      <MatrixCell
        value={0}
        levelClass='diff empty'
        tooltip={{
          title: `${comp.display} · ${fn.display}`,
          body: 'No roadmap demand on this context.',
        }}
        extraClass={`is-merged${comp.is_overlay ? ' is-overlay-cell' : ''}`}
      />
    );
  }

  const { count: supply, qualified } = countQualifiedEngineers(cell, ctx);
  const demand = cell.count;
  const diff = supply - demand;
  const level = diff < 0 ? negLevel(Math.abs(diff)) : posLevel(diff);
  const sign = diff > 0 ? '+' : '';
  const levelClass = `diff ${diff < 0 ? 'neg' : 'pos'} lvl-${level}`;

  const items = isAggregate
    ? (cell.contributions ?? [])
    : cell.demands.map(d => ({ project: projectDisplay, ...d }));

  const lines = items
    .map(it => {
      const skillDisplay = skillDisplayById.get(it.skill) ?? it.skill;
      const roleNote = it.role && it.role !== 'primary' ? ` (${it.role})` : '';
      const projNote = isAggregate ? ` — ${it.project}` : '';
      return `  ${skillDisplay} · ${RATING_LABELS[it.level]}${roleNote}${projNote}`;
    })
    .join('\n');

  const maxLevel = cell.maxLevel as RatingValue;
  const body = [
    `Supply ${supply} · Demand ${demand}`,
    `Strictest bar: ${RATING_LABELS[maxLevel]}`,
    lines ? `Demands:\n${lines}` : null,
    qualified.length > 0
      ? `Qualified: ${qualified.join(', ')}`
      : 'Qualified: (none clear the strictest bar)',
  ]
    .filter(Boolean)
    .join('\n');

  // MatrixCell renders any non-null `value` via `String(value)`; pass the
  // signed-int text directly. The DiffCell's cast preserves the visible
  // sign character ("+", "−") which a raw `number` would otherwise drop.
  return (
    <MatrixCell
      value={`${sign}${diff}` as unknown as number}
      levelClass={levelClass}
      extraClass={`is-merged${comp.is_overlay ? ' is-overlay-cell' : ''}`}
      tooltip={{
        title: `${comp.display} · ${fn.display}`,
        body,
      }}
    />
  );
}
